import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Users, BookOpen, Eye, Archive, Edit3, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ALL_NAV_KEYS = [
    { key: "dashboard", label: "Dashboard" },
    { key: "start-here", label: "Start Here" },
    { key: "modules", label: "Modules" },
    { key: "flashcards", label: "Flash Cards" },
    { key: "quizzes", label: "Quizzes" },
    { key: "certifications", label: "Certifications" },
    { key: "resources", label: "Resources" },
    { key: "roleplays", label: "Roleplay Library" },
    { key: "progress", label: "Progress" },
];

const fld = "w-full bg-white border border-ink-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-haevn-700 transition-colors";
const lbl = "block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5";

export default function AdminGroups() {
    const [groups, setGroups] = useState([]);
    const [selected, setSelected] = useState(null); // slug
    const [detail, setDetail] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [users, setUsers] = useState([]);
    const [modules, setModules] = useState([]);
    const { refreshMe, bumpNav } = useAuth();

    const loadGroups = () => api.get("/admin/groups").then((r) => {
        setGroups(r.data);
        if (!selected && r.data.length > 0) setSelected(r.data[0].slug);
    });
    useEffect(() => { loadGroups(); }, []);
    useEffect(() => {
        api.get("/admin/users").then((r) => setUsers(r.data));
        api.get("/admin/modules").then((r) => setModules(r.data));
    }, []);
    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        let cancelled = false;
        api.get(`/admin/groups/${selected}`).then((r) => {
            if (!cancelled) setDetail(r.data);
        });
        return () => { cancelled = true; };
    }, [selected]);

    // `refresh` only updates the group LIST + the detail of whatever group is
    // CURRENTLY selected at call time. It explicitly does NOT pin to a stale
    // selected — the useEffect above handles the detail when selected changes.
    const refresh = (newSelected) => {
        loadGroups();
        if (newSelected !== undefined && newSelected !== selected) {
            setSelected(newSelected);
            return; // the useEffect will fetch detail for the new slug
        }
        if (selected) {
            api.get(`/admin/groups/${selected}`).then((r) => setDetail(r.data));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-groups-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-ink-500">Define who sees what. Groups gate module access and tailor the sidebar.</p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    data-testid="admin-create-group-btn"
                    className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900"
                >
                    <Plus size={14} /> New group
                </button>
            </div>

            <div className="grid lg:grid-cols-[320px_1fr] gap-px bg-ink-300 border border-ink-300">
                {/* Group list */}
                <div className="bg-white">
                    <div className="px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono border-b border-ink-200">
                        Groups
                    </div>
                    {groups.length === 0 ? (
                        <div className="px-5 py-6 text-sm text-ink-500">No groups yet. Create the first one.</div>
                    ) : groups.map((g) => (
                        <button
                            key={g.slug}
                            onClick={() => setSelected(g.slug)}
                            data-testid={`admin-group-row-${g.slug}`}
                            className={`w-full text-left px-5 py-3 border-b border-ink-200 last:border-0 transition-colors ${
                                selected === g.slug ? "bg-haevn-50" : "hover:bg-ink-100"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-ink-900">{g.title}</span>
                                {g.is_archived && <span className="text-[10px] text-ink-400 font-mono uppercase tracking-[0.15em]">Archived</span>}
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-[11px] text-ink-500">
                                <span className="inline-flex items-center gap-1"><Users size={11} /> {g.member_count} members</span>
                                <span className="inline-flex items-center gap-1"><BookOpen size={11} /> {g.module_count} modules</span>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Detail */}
                <div className="bg-white">
                    {!detail ? (
                        <div className="p-8 text-sm text-ink-500">Select a group on the left to view details.</div>
                    ) : (
                        <GroupDetail
                            group={detail}
                            allUsers={users}
                            allModules={modules}
                            onChanged={refresh}
                            onPreviewActivated={refreshMe}
                            onNavChanged={bumpNav}
                        />
                    )}
                </div>
            </div>

            {showCreate && (
                <CreateGroupModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(slug) => { setShowCreate(false); refresh(slug); }}
                />
            )}
        </div>
    );
}

function GroupDetail({ group, allUsers, allModules, onChanged, onPreviewActivated, onNavChanged }) {
    const [editing, setEditing] = useState(false);
    const [navKeys, setNavKeys] = useState(group.nav_keys || []);
    const [title, setTitle] = useState(group.title);
    const [description, setDescription] = useState(group.description || "");
    const [purpose, setPurpose] = useState(group.purpose || "");
    const [savingNav, setSavingNav] = useState(false);
    const [showAddMembers, setShowAddMembers] = useState(false);
    const [showAssignModules, setShowAssignModules] = useState(false);

    useEffect(() => {
        setNavKeys(group.nav_keys || []);
        setTitle(group.title);
        setDescription(group.description || "");
        setPurpose(group.purpose || "");
        setEditing(false);
    }, [group.slug]);

    // Save title + description + purpose (triggered by the Save button).
    const saveDetails = async () => {
        try {
            await api.patch(`/admin/groups/${group.slug}`, { title, description, purpose });
            toast.success("Group updated");
            setEditing(false);
            onChanged();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    // Toggling a sidebar nav-key auto-saves — no Edit gate. This is the visibility
    // control admins use most often, so we make it direct and immediate.
    const toggleNavKey = async (key) => {
        const next = navKeys.includes(key)
            ? navKeys.filter((k) => k !== key)
            : [...navKeys, key];
        setNavKeys(next);
        setSavingNav(true);
        try {
            await api.patch(`/admin/groups/${group.slug}`, { nav_keys: next });
            onChanged();
            onNavChanged?.();
        } catch (e) {
            // Roll back the optimistic update on failure
            setNavKeys(navKeys);
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
        } finally {
            setSavingNav(false);
        }
    };

    const archive = async () => {
        if (!window.confirm(`Archive group "${group.title}"? Members will lose this group's access.`)) return;
        try {
            await api.delete(`/admin/groups/${group.slug}`);
            toast.success("Group archived");
            onChanged();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const removeMember = async (userId) => {
        try {
            await api.post(`/admin/groups/${group.slug}/members/remove`, { user_ids: [userId] });
            onChanged();
        } catch (e) {
            toast.error("Failed to remove");
        }
    };

    const previewAs = async () => {
        try {
            await api.post(`/admin/preview-as`, { group_ids: [group.slug] });
            toast.success(`Previewing as "${group.title}"`);
            onPreviewActivated();
        } catch (e) {
            toast.error("Failed to enter preview");
        }
    };

    const memberIds = useMemo(() => new Set((group.members || []).map((m) => m.id)), [group.members]);
    const moduleIds = useMemo(() => new Set((group.modules || []).map((m) => m.id)), [group.modules]);

    return (
        <div className="p-6 space-y-8" data-testid={`admin-group-detail-${group.slug}`}>
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {editing ? (
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={`${fld} text-xl font-editorial`}
                            data-testid="group-edit-title"
                        />
                    ) : (
                        <h2 className="font-editorial text-3xl text-ink-900">{group.title}</h2>
                    )}
                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">
                        slug: {group.slug}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={previewAs}
                        data-testid={`group-preview-btn-${group.slug}`}
                        className="inline-flex items-center gap-1.5 border border-ink-300 px-3 py-1.5 rounded-sm text-xs hover:border-haevn-700"
                    >
                        <Eye size={13} /> Preview as
                    </button>
                    {editing ? (
                        <button onClick={saveDetails} className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-3 py-1.5 rounded-sm text-xs hover:bg-haevn-900" data-testid="group-save-btn">
                            <Check size={13} /> Save
                        </button>
                    ) : (
                        <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 border border-ink-300 px-3 py-1.5 rounded-sm text-xs hover:border-haevn-700" data-testid="group-edit-btn">
                            <Edit3 size={13} /> Edit details
                        </button>
                    )}
                    {!group.is_archived && (
                        <button onClick={archive} className="inline-flex items-center gap-1.5 border border-clay-50 text-clay-500 px-3 py-1.5 rounded-sm text-xs hover:bg-clay-50" data-testid="group-archive-btn">
                            <Archive size={13} /> Archive
                        </button>
                    )}
                </div>
            </div>

            {/* Description */}
            <div>
                <div className={lbl}>Description</div>
                {editing ? (
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        className={fld}
                        placeholder="Who this group is — for admin context."
                        data-testid="group-edit-description"
                    />
                ) : (
                    <p className="text-sm text-ink-700">{group.description || <span className="text-ink-400">No description</span>}</p>
                )}
            </div>

            {/* Purpose / Intent — used as dashboard subtitle for this group's members */}
            <div>
                <div className={lbl}>Purpose / intent</div>
                {editing ? (
                    <>
                        <textarea
                            value={purpose}
                            onChange={(e) => setPurpose(e.target.value)}
                            rows={2}
                            className={fld}
                            placeholder="A one-line statement of what this group should do or feel. Shown as their dashboard subtitle."
                            data-testid="group-edit-purpose"
                        />
                        <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">
                            Shown as the subtitle on this group's dashboard.
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-ink-700 italic" data-testid="group-purpose-display">
                        {group.purpose || <span className="text-ink-400 not-italic">No purpose set</span>}
                    </p>
                )}
            </div>

            {/* Sidebar nav visibility */}
            <div>
                <div className="flex items-center justify-between">
                    <div className={lbl}>Sidebar nav visibility</div>
                    {savingNav && (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">Saving…</span>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {ALL_NAV_KEYS.map(({ key, label }) => {
                        const checked = navKeys.includes(key);
                        return (
                            <label key={key} className={`flex items-center gap-2 px-3 py-2 border rounded-sm text-sm cursor-pointer transition-colors ${
                                checked ? "border-haevn-700 bg-haevn-50" : "border-ink-300 hover:border-ink-500"
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleNavKey(key)}
                                    data-testid={`group-nav-${key}`}
                                />
                                {label}
                            </label>
                        );
                    })}
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">
                    Check or uncheck to control what this group sees in the sidebar. Changes save instantly.
                </div>
            </div>

            {/* Members */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className={lbl}>Members ({group.members?.length || 0})</div>
                    <button
                        onClick={() => setShowAddMembers(true)}
                        data-testid="group-add-members-btn"
                        className="text-xs text-haevn-700 hover:text-haevn-900 inline-flex items-center gap-1"
                    >
                        <Plus size={12} /> Add members
                    </button>
                </div>
                <div className="border border-ink-300 rounded-sm divide-y divide-ink-200 max-h-72 overflow-y-auto scrollbar-thin">
                    {(group.members || []).length === 0 ? (
                        <div className="px-4 py-6 text-sm text-ink-500 text-center">No members yet.</div>
                    ) : group.members.map((m) => (
                        <div key={m.id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`group-member-${m.id}`}>
                            <div className="min-w-0">
                                <div className="text-ink-900 truncate">{m.name}</div>
                                <div className="text-xs text-ink-500 truncate">{m.email}</div>
                            </div>
                            <button
                                onClick={() => removeMember(m.id)}
                                className="text-[10px] uppercase tracking-[0.15em] text-ink-400 hover:text-clay-500 font-mono"
                                data-testid={`group-member-remove-${m.id}`}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modules */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className={lbl}>Modules this group can access ({group.modules?.length || 0})</div>
                    <button
                        onClick={() => setShowAssignModules(true)}
                        data-testid="group-assign-modules-btn"
                        className="text-xs text-haevn-700 hover:text-haevn-900 inline-flex items-center gap-1"
                    >
                        <Edit3 size={12} /> Edit access
                    </button>
                </div>
                <div className="border border-ink-300 rounded-sm divide-y divide-ink-200 max-h-72 overflow-y-auto scrollbar-thin">
                    {(group.modules || []).length === 0 ? (
                        <div className="px-4 py-6 text-sm text-ink-500 text-center">No modules assigned yet.</div>
                    ) : group.modules.map((m) => (
                        <div key={m.id} className="px-4 py-2 text-sm text-ink-900" data-testid={`group-module-${m.id}`}>
                            {m.title}
                        </div>
                    ))}
                </div>
            </div>

            {showAddMembers && (
                <AddMembersModal
                    group={group}
                    allUsers={allUsers}
                    currentMemberIds={memberIds}
                    onClose={() => setShowAddMembers(false)}
                    onSaved={() => { setShowAddMembers(false); onChanged(); }}
                />
            )}
            {showAssignModules && (
                <AssignModulesModal
                    group={group}
                    allModules={allModules}
                    currentModuleIds={moduleIds}
                    onClose={() => setShowAssignModules(false)}
                    onSaved={() => { setShowAssignModules(false); onChanged(); onNavChanged?.(); }}
                />
            )}
        </div>
    );
}

function AddMembersModal({ group, allUsers, currentMemberIds, onClose, onSaved }) {
    const [selected, setSelected] = useState([]);
    const [q, setQ] = useState("");
    const candidates = useMemo(
        () => allUsers
            .filter((u) => (u.status || "active") !== "archived")
            .filter((u) => !currentMemberIds.has(u.id))
            .filter((u) => !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())),
        [allUsers, currentMemberIds, q],
    );
    const submit = async () => {
        if (selected.length === 0) { onClose(); return; }
        try {
            await api.post(`/admin/groups/${group.slug}/members`, { user_ids: selected });
            toast.success(`${selected.length} member${selected.length === 1 ? "" : "s"} added`);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };
    return (
        <ModalShell title={`Add members to ${group.title}`} onClose={onClose} testId="add-members-modal">
            <input
                placeholder="Search name or email…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className={`${fld} mb-3`}
                data-testid="add-members-search"
            />
            <div className="border border-ink-300 rounded-sm max-h-80 overflow-y-auto scrollbar-thin">
                {candidates.length === 0 ? (
                    <div className="p-4 text-sm text-ink-500 text-center">No users to add.</div>
                ) : candidates.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 px-4 py-2 hover:bg-ink-100 cursor-pointer border-b border-ink-200 last:border-0">
                        <input
                            type="checkbox"
                            checked={selected.includes(u.id)}
                            onChange={(e) => {
                                if (e.target.checked) setSelected([...selected, u.id]);
                                else setSelected(selected.filter((x) => x !== u.id));
                            }}
                            data-testid={`add-member-${u.id}`}
                        />
                        <div className="min-w-0">
                            <div className="text-sm text-ink-900 truncate">{u.name}</div>
                            <div className="text-xs text-ink-500 truncate">{u.email}</div>
                        </div>
                    </label>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
                <button onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm">Cancel</button>
                <button onClick={submit} disabled={selected.length === 0} data-testid="add-members-submit"
                    className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50">
                    Add {selected.length || ""}
                </button>
            </div>
        </ModalShell>
    );
}

function AssignModulesModal({ group, allModules, currentModuleIds, onClose, onSaved }) {
    const [selected, setSelected] = useState(new Set(currentModuleIds));
    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const submit = async () => {
        try {
            // Compute diff: modules to add this group_id to, modules to remove from
            const toAdd = [...selected].filter((id) => !currentModuleIds.has(id));
            const toRemove = [...currentModuleIds].filter((id) => !selected.has(id));
            // For each module, refetch current group_ids and patch
            await Promise.all([
                ...toAdd.map(async (id) => {
                    const m = allModules.find((x) => x.id === id);
                    const groupIds = Array.from(new Set([...(m?.group_ids || []), group.slug]));
                    await api.put(`/admin/modules/${id}/groups`, { group_ids: groupIds });
                }),
                ...toRemove.map(async (id) => {
                    const m = allModules.find((x) => x.id === id);
                    const groupIds = (m?.group_ids || []).filter((g) => g !== group.slug);
                    await api.put(`/admin/modules/${id}/groups`, { group_ids: groupIds });
                }),
            ]);
            toast.success("Module access updated");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };
    return (
        <ModalShell title={`Module access — ${group.title}`} onClose={onClose} testId="assign-modules-modal" wide>
            <p className="text-sm text-ink-500 mb-3">
                Check the modules members of <span className="text-ink-900">{group.title}</span> should be able to see.
            </p>
            <div className="border border-ink-300 rounded-sm max-h-96 overflow-y-auto scrollbar-thin">
                {allModules.length === 0 ? (
                    <div className="p-4 text-sm text-ink-500 text-center">No modules in the system yet.</div>
                ) : allModules.map((m) => (
                    <label key={m.id} className="flex items-center gap-3 px-4 py-2 hover:bg-ink-100 cursor-pointer border-b border-ink-200 last:border-0">
                        <input
                            type="checkbox"
                            checked={selected.has(m.id)}
                            onChange={() => toggle(m.id)}
                            data-testid={`assign-module-${m.id}`}
                        />
                        <div className="min-w-0">
                            <div className="text-sm text-ink-900 truncate">{m.title}</div>
                            {m.group_ids?.length > 0 && (
                                <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mt-0.5">
                                    in: {m.group_ids.join(", ")}
                                </div>
                            )}
                        </div>
                    </label>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
                <button onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm">Cancel</button>
                <button onClick={submit} data-testid="assign-modules-submit"
                    className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900">
                    Save access
                </button>
            </div>
        </ModalShell>
    );
}

function CreateGroupModal({ onClose, onCreated }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [navKeys, setNavKeys] = useState(["dashboard", "modules", "flashcards", "quizzes", "certifications", "resources", "progress"]);
    const submit = async (e) => {
        e.preventDefault();
        const f = e.target;
        setBusy(true); setError("");
        try {
            const { data } = await api.post(`/admin/groups`, {
                title: f.title.value,
                slug: f.slug.value || undefined,
                description: f.description.value || "",
                purpose: f.purpose.value || "",
                nav_keys: navKeys,
            });
            toast.success("Group created");
            onCreated(data.slug);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title="Create a new group" onClose={onClose} testId="create-group-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Group name</label><input className={fld} name="title" required data-testid="create-group-title" placeholder="Field Sales" /></div>
                    <div><label className={lbl}>Slug (optional)</label><input className={fld} name="slug" data-testid="create-group-slug" placeholder="field-sales" /></div>
                </div>
                <div>
                    <label className={lbl}>Description</label>
                    <textarea
                        className={fld}
                        name="description"
                        rows={2}
                        data-testid="create-group-description"
                        placeholder="Who this group is — for admin context."
                    />
                </div>
                <div>
                    <label className={lbl}>Purpose / intent</label>
                    <textarea
                        className={fld}
                        name="purpose"
                        rows={2}
                        data-testid="create-group-purpose"
                        placeholder="A one-line statement of what this group should do or feel."
                    />
                    <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">
                        Shown as the subtitle on this group's dashboard.
                    </div>
                </div>
                <div>
                    <label className={lbl}>Sidebar nav visibility</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {ALL_NAV_KEYS.map(({ key, label }) => {
                            const checked = navKeys.includes(key);
                            return (
                                <label key={key} className={`flex items-center gap-2 px-3 py-2 border rounded-sm text-sm cursor-pointer transition-colors ${
                                    checked ? "border-haevn-700 bg-haevn-50" : "border-ink-300 hover:border-ink-500"
                                }`}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                            if (e.target.checked) setNavKeys([...navKeys, key]);
                                            else setNavKeys(navKeys.filter((k) => k !== key));
                                        }}
                                        data-testid={`create-group-nav-${key}`}
                                    />
                                    {label}
                                </label>
                            );
                        })}
                    </div>
                </div>
                {error && <div className="text-xs text-clay-500 bg-clay-50 px-3 py-2 rounded-sm">{error}</div>}
                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="create-group-submit"
                        className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50">
                        {busy ? "Creating…" : "Create group"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ModalShell({ title, onClose, children, testId, wide }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className={`bg-white border border-ink-300 ${wide ? "max-w-3xl" : "max-w-2xl"} w-full max-h-[90vh] overflow-y-auto p-8`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="flex items-start justify-between mb-6">
                    <h2 className="font-editorial text-2xl">{title}</h2>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-900"><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
